import React from "react";
import { useState, useEffect, useCallback } from "react";
import mondaySdk from "monday-sdk-js";
import { useNavigate } from "react-router-dom";
import { CheckOutlined } from "@ant-design/icons";
import { Button } from "@vibe/core";
import { DropdownChevronRight } from "@vibe/icons";
import { useCheckUserOnboarding } from "../../hooks/api/useCheckUserOnboarding";
import { ConsentCheckboxes } from "../../components/LegalConsent";
import useConsent from "../../hooks/api/useConsent";
import { canSubmit } from "../../utils/consentModel";
import { CONSENT_SOURCE_ONBOARDING } from "../../data/constants/legalPolicies";
import type { ConsentSelections } from "../../utils/consentTypes";
import useMondayContext from "@hooks/context/useMondayContext";

const backend_uri = process.env.REACT_APP_URI;

const monday = mondaySdk();

type StepStatus = "completed" | "current" | "upcoming";

interface Step {
  number: number;
  name: string;
  status: StepStatus;
}

const AUTHORIZE_STEPS: Step[] = [
  { number: 1, name: "Install", status: "completed" },
  { number: 2, name: "Authorize", status: "current" },
  { number: 3, name: "Start Sharing", status: "upcoming" },
];

const ProgressStepper = ({ steps }: { steps: Step[] }) => {
  const getStepStyles = (status: StepStatus) => {
    switch (status) {
      case "completed":
        return {
          circle: "bg-positive text-white border-positive",
          text: "text-positive font-medium",
          line: "bg-positive",
        };
      case "current":
        return {
          circle: "bg-primary-color text-white border-primary-color",
          text: "text-primary-color font-medium",
          line: "bg-gray-300",
        };
      case "upcoming":
        return {
          circle: "!bg-transparent text-gray-400 !border-gray-400",
          text: "text-gray-400",
          line: "bg-gray-300",
        };
    }
  };

  return (
    <div className="flex justify-center items-start mb-6 w-full">
      {steps.map((step, index) => {
        const styles = getStepStyles(step.status);
        const isLast = index === steps.length - 1;

        return (
          <div key={step.number} className="flex items-start">
            {/* Step circle and label */}
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 border-solid text-sm font-semibold ${styles.circle}`}
              >
                {step.status === "completed" ? <CheckOutlined className="text-sm" /> : step.number}
              </div>
              <span className={`mt-2 text-xs !font-figtree whitespace-nowrap ${styles.text}`}>
                {step.name}
              </span>
            </div>

            {/* Connector line */}
            {!isLast && (
              <div className="flex items-center h-8">
                <div className={`w-16 h-[2px] mx-2 ${styles.line}`} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const AuthorizePage = () => {
  const [userid, setUserid] = useState<number | false>(false);
  const [appFeature, setAppFeature] = useState<string>();
  const [authUrl, setAuthUrl] = useState("");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [fallbackAuthUrl, setFallbackAuthUrl] = useState("");
  const { consentState, submitConsent, submitting } = useConsent();
  const [consent, setConsent] = useState<ConsentSelections>({
    terms: false,
    privacy: false,
    marketing: false,
  });
  const { appVersionId } = useMondayContext();

  const monday_app_version_id = appVersionId ? appVersionId.toString() : null;

  const navigate = useNavigate();

  // Pre-check from any prior decision (returning/already-consented user).
  useEffect(() => {
    setConsent({
      terms: consentState.termsGranted,
      privacy: consentState.privacyGranted,
      marketing: consentState.marketingGranted,
    });
  }, [consentState.termsGranted, consentState.privacyGranted, consentState.marketingGranted]);

  const handleAuthorized = useCallback(() => {
    // Clear any pending redirect since we're handling it here
    localStorage.removeItem("pendingAuthRedirect");

    const routes: Record<string, string> = {
      AppFeatureItemMenuAction: "/board-item-menu",
      AppFeatureItemBatchAction: "/batch-items-menu",
      AppFeatureBoardView: "/resynchronization",
      AppFeatureBoardColumnExtension: "/add-column",
      AppFeatureDialog: "/boards-dialog",
    };

    const route = appFeature ? routes[appFeature] : "/?tour=true";
    navigate(route || "/?tour=true");
  }, [appFeature, navigate]);

  const { startPolling } = useCheckUserOnboarding(userid || undefined, {
    skipNavigation: true,
    onAuthorized: handleAuthorized,
  });

  useEffect(() => {
    monday.get("context").then((res) => {
      setUserid(res.data.user.id);
      setAppFeature(res.data.appFeature.type);
    });
  }, [appFeature]);

  useEffect(() => {
    // Route through the backend's PKCE initiator instead of hitting
    // auth.monday.com directly — the backend sets up the code_challenge and
    // stores the verifier, which is what makes Monday return a refresh token.
    const iframeParams = new URLSearchParams({ source: "iframe" });
    const newtabParams = new URLSearchParams({ source: "newtab" });

    if (monday_app_version_id) {
      iframeParams.set("app_version_id", monday_app_version_id);
      newtabParams.set("app_version_id", monday_app_version_id);
    }

    setAuthUrl(`${backend_uri}/oauth/authorize?${iframeParams.toString()}`);
    setFallbackAuthUrl(`${backend_uri}/oauth/authorize?${newtabParams.toString()}`);
  }, [monday_app_version_id]);

  const authorize = () => {
    // Store appFeature before OAuth so we can redirect to the correct page after auth
    if (appFeature) {
      localStorage.setItem("pendingAuthRedirect", appFeature);
    }
    startPolling();
    navigate("/onboarding/mondayOAuth", { state: { authUrl } });
  };

  // Record consent first; only start OAuth if it succeeds.
  const handleAuthorizeClick = async () => {
    try {
      await submitConsent(consent, CONSENT_SOURCE_ONBOARDING);
    } catch {
      monday.execute("notice", {
        message: "Failed to record your consent. Please try again.",
        type: "error",
        timeout: 5000,
      });
      return;
    }
    authorize();
  };

  return (
    <section className="pt-10">
      {/* <div className="flex items-center justify-center w-full max-h-[225px] bg-[#1D7B68]">
        <img src="/img/simb-logo.png" alt="SIMB Logo" className="max-h-[225px] w-full object-contain" />
      </div> */}

      <div className="py-5">
        <div className="mx-auto text-center max-w-[560px]">
          <div className="mb-8">
            <h3 className="!font-figtree font-semibold">Welcome to Same Item Multiple Boards</h3>
            {/* <p className="!font-figtree text-sm !mt-2">
              Click on “Authorize Monday” to start adding items <br /> to multiple boards with Same Item on Multiple Boards
            </p> */}
          </div>

          {/* Progress Stepper */}

          <div className="mb-8">
            <ProgressStepper steps={AUTHORIZE_STEPS} />
          </div>

          <div className="mb-6">
            <ConsentCheckboxes value={consent} onChange={setConsent} />
          </div>

          <div className="flex gap-2 justify-center items-center mb-4">
            <Button
              color="primary"
              onClick={handleAuthorizeClick}
              disabled={!canSubmit(consent) || submitting}
              loading={submitting}
            >
              <img width={24} src="/img/monday.png" alt="monday icon" className="mr-2" />
              Authorize monday
            </Button>
            <DropdownChevronRight
              size={24}
              className="text-primary-color text-xl animate-[bounce-horizontal_1s_ease-in-out_infinite] "
            />
          </div>

          {/* {fallbackAuthUrl && (
            <p className="text-xs text-gray-500 !font-figtree">
              Having trouble?{" "}
              <a
                href={fallbackAuthUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-primary-color hover:text-primary-hover-color"
                onClick={() => startPolling()}
              >
                Click here if authorization fails.
              </a>
            </p>
          )} */}
        </div>
      </div>
    </section>
  );
};

export default AuthorizePage;
